import DeleteConfirmPopover from "components/common/DeleteConfirmPopover";
import {
  AskStrikeUpButton,
  Badge,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  Input,
  RefreshButton,
  Stack,
  Text,
  Textarea,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useMemo, useRef, useState } from "react";
import { MdAdd, MdAutoAwesome, MdEdit, MdSearch, MdSend } from "react-icons/md";
import {
  Skill,
  SkillCompilerResult,
  WizardMessage,
  skillService,
} from "services/skillService";
import { getThemeColor } from "theme/colors";

type PanelMode = "none" | "create" | "view" | "edit";

// Must stay in sync with the Skill entity column widths (SkillConfiguration.cs)
// and the CreateSkillRequest/UpdateSkillRequest annotations. Exceeding them is
// rejected by the API with a 400 — these caps stop the user getting there.
const SKILL_NAME_MAX = 200;
const SKILL_CATEGORY_MAX = 100;
const SKILL_TRIGGER_MAX = 2000;

/** Right-aligned "used / limit" counter for length-capped fields. */
const CharCount = ({ value, max }: { value: string; max: number }) => (
  <Text
    fontSize="xs"
    alignSelf="flex-end"
    color={value.length >= max ? "orange.500" : getThemeColor("text.secondary")}
  >
    {value.length} / {max}
  </Text>
);

const Skills = () => {
  const { t } = useTranslation();

  // Left panel
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loadingSkills, setLoadingSkills] = useState(true);
  const [skillsError, setSkillsError] = useState<string | null>(null);
  const [skillSearch, setSkillSearch] = useState("");
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);

  // Panel
  const [panelMode, setPanelMode] = useState<PanelMode>("none");

  // Create: which sub-panel — AI wizard or manual form
  const [createMode, setCreateMode] = useState<"ai" | "manual">("ai");

  // Create: wizard chat state
  const READY_MARKER = "[READY_TO_CREATE]";
  const [wizardMessages, setWizardMessages] = useState<WizardMessage[]>([]);
  const [wizardInput, setWizardInput] = useState("");
  const [wizardLoading, setWizardLoading] = useState(false);
  const [compiledResult, setCompiledResult] =
    useState<SkillCompilerResult | null>(null);
  const [saving, setSaving] = useState(false);
  const [panelError, setPanelError] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const lastMsg = wizardMessages[wizardMessages.length - 1];
  const isReadyToCreate =
    lastMsg?.role === "assistant" && lastMsg.content.includes(READY_MARKER);

  // Create: manual form state
  const [manualName, setManualName] = useState("");
  const [manualCategory, setManualCategory] = useState("");
  const [manualDescription, setManualDescription] = useState("");
  const [manualTrigger, setManualTrigger] = useState("");
  const [manualSkillPrompt, setManualSkillPrompt] = useState("");
  const [manualSaving, setManualSaving] = useState(false);

  // Edit state
  const [editMode, setEditMode] = useState<"manual" | "ai">("ai");
  const [editSkill, setEditSkill] = useState<Skill | null>(null);
  const [editDescription, setEditDescription] = useState("");
  const [editName, setEditName] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editTrigger, setEditTrigger] = useState("");
  const [editSkillPrompt, setEditSkillPrompt] = useState("");
  const [changeRequest, setChangeRequest] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadSkills = async () => {
    try {
      setLoadingSkills(true);
      const data = await skillService.getSkills();
      setSkills(data);
      setSkillsError(null);
    } catch (err: any) {
      setSkillsError(err.response?.data?.message || t("app.skills.loadFailed"));
    } finally {
      setLoadingSkills(false);
    }
  };

  useEffect(() => {
    loadSkills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Client-side filter: name, category, trigger and description all match.
  const filteredSkills = useMemo(() => {
    const query = skillSearch.trim().toLowerCase();
    if (!query) return skills;
    return skills.filter((skill) =>
      [
        skill.name,
        skill.category,
        skill.triggerContext,
        skill.humanReadableDescription,
      ].some((field) => field?.toLowerCase().includes(query)),
    );
  }, [skills, skillSearch]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [wizardMessages]);

  // ── Create flow ──────────────────────────────────────────────────────────────

  const startAiCreate = () => {
    setPanelMode("create");
    setCreateMode("ai");
    setSelectedSkillId(null);
    setWizardMessages([
      {
        role: "assistant",
        content:
          "Tell me what you'd like this skill to help your agent do. What situations or questions should it handle?",
      },
    ]);
    setWizardInput("");
    setCompiledResult(null);
    setPanelError(null);
  };

  const startManualCreate = () => {
    setPanelMode("create");
    setCreateMode("manual");
    setSelectedSkillId(null);
    setManualName("");
    setManualCategory("");
    setManualDescription("");
    setManualTrigger("");
    setManualSkillPrompt("");
    setPanelError(null);
  };

  const handleManualCreateSave = async () => {
    const name = manualName.trim();
    const description = manualDescription.trim();
    const trigger = manualTrigger.trim();
    const skillPrompt = manualSkillPrompt.trim();

    if (!name) {
      setPanelError(t("app.skills.nameRequired"));
      return;
    }
    if (!description) {
      setPanelError(t("app.skills.descriptionRequired"));
      return;
    }
    if (!trigger) {
      setPanelError(t("app.skills.triggerRequired"));
      return;
    }
    if (!skillPrompt) {
      setPanelError(t("app.skills.skillPromptRequired"));
      return;
    }

    setManualSaving(true);
    setPanelError(null);
    try {
      const created = await skillService.createSkill({
        name,
        category: manualCategory.trim() || undefined,
        humanReadableDescription: description,
        triggerContext: trigger,
        skillPrompt,
      });
      await loadSkills();
      openEdit(created);
    } catch (err: any) {
      setPanelError(err.response?.data?.message || t("app.skills.saveFailed"));
    } finally {
      setManualSaving(false);
    }
  };

  const sendWizardMessage = async () => {
    const text = wizardInput.trim();
    if (!text || wizardLoading) return;

    const newMessages: WizardMessage[] = [
      ...wizardMessages,
      { role: "user", content: text },
    ];
    setWizardMessages(newMessages);
    setWizardInput("");
    setWizardLoading(true);
    setPanelError(null);

    try {
      const response = await skillService.wizardMessage(newMessages);
      setWizardMessages((prev) => [
        ...prev,
        { role: "assistant", content: response.message },
      ]);
    } catch (err: any) {
      setPanelError(
        err.response?.data?.message || t("app.skills.wizard.failed"),
      );
    } finally {
      setWizardLoading(false);
    }
  };

  const handleCompileAndSave = async () => {
    setSaving(true);
    setPanelError(null);
    try {
      const compiled = await skillService.compileSkill({
        conversation: wizardMessages,
      });
      const created = await skillService.createSkill({
        name: compiled.name?.trim() || "New Skill",
        category: compiled.category?.trim() || undefined,
        humanReadableDescription: compiled.humanReadableDescription,
        triggerContext: compiled.triggerContext,
        skillPrompt: compiled.skillPrompt,
      });
      await loadSkills();
      openEdit(created);
    } catch (err: any) {
      setPanelError(err.response?.data?.message || t("app.skills.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  // ── Edit flow ────────────────────────────────────────────────────────────────

  const openEdit = (skill: Skill) => {
    setPanelMode("edit");
    setEditMode("ai");
    setSelectedSkillId(skill.id);
    setEditSkill(skill);
    setEditName(skill.name);
    setEditCategory(skill.category ?? "");
    setEditDescription(skill.humanReadableDescription);
    setEditTrigger(skill.triggerContext);
    setEditSkillPrompt(skill.skillPrompt);
    setChangeRequest("");
    setPanelError(null);
    setCompiledResult(null);
  };

  const handleManualEditSave = async () => {
    if (!editSkill) return;

    const name = editName.trim();
    const description = editDescription.trim();
    const trigger = editTrigger.trim();
    const skillPrompt = editSkillPrompt.trim();

    if (!name) {
      setPanelError(t("app.skills.nameRequired"));
      return;
    }
    if (!description) {
      setPanelError(t("app.skills.descriptionRequired"));
      return;
    }
    if (!trigger) {
      setPanelError(t("app.skills.triggerRequired"));
      return;
    }
    if (!skillPrompt) {
      setPanelError(t("app.skills.skillPromptRequired"));
      return;
    }

    setEditSaving(true);
    setPanelError(null);
    try {
      const updated = await skillService.updateSkill(editSkill.id, {
        name,
        category: editCategory.trim() || undefined,
        humanReadableDescription: description,
        triggerContext: trigger,
        skillPrompt,
      });
      setEditSkill(updated);
      setEditName(updated.name);
      setEditCategory(updated.category ?? "");
      setEditDescription(updated.humanReadableDescription);
      setEditTrigger(updated.triggerContext);
      setEditSkillPrompt(updated.skillPrompt);
      await loadSkills();
    } catch (err: any) {
      setPanelError(err.response?.data?.message || t("app.skills.saveFailed"));
    } finally {
      setEditSaving(false);
    }
  };

  const handleSkillSelect = (skill: Skill) => {
    setPanelMode("view");
    setSelectedSkillId(skill.id);
    setEditSkill(skill);
    setPanelError(null);
  };

  const handleEditSave = async () => {
    if (!editSkill) return;

    const descChanged = editDescription !== editSkill.humanReadableDescription;
    const hasChangeRequest = changeRequest.trim().length > 0;

    if (!descChanged && !hasChangeRequest) {
      // Only name/category changed — save directly without recompile
      setEditSaving(true);
      setPanelError(null);
      try {
        const updated = await skillService.updateSkill(editSkill.id, {
          name: editName.trim(),
          category: editCategory.trim() || undefined,
        });
        setEditSkill(updated);
        await loadSkills();
      } catch (err: any) {
        setPanelError(
          err.response?.data?.message || t("app.skills.saveFailed"),
        );
      } finally {
        setEditSaving(false);
      }
      return;
    }

    // Recompile then save
    setEditSaving(true);
    setPanelError(null);
    try {
      const compiled = await skillService.compileSkill({
        currentDescription: editDescription,
        changeRequest: hasChangeRequest ? changeRequest.trim() : undefined,
      });
      const updated = await skillService.updateSkill(editSkill.id, {
        name: editName.trim(),
        category: editCategory.trim() || undefined,
        humanReadableDescription: compiled.humanReadableDescription,
        triggerContext: compiled.triggerContext,
        skillPrompt: compiled.skillPrompt,
      });
      setEditSkill(updated);
      setEditDescription(updated.humanReadableDescription);
      setChangeRequest("");
      setCompiledResult(compiled);
      await loadSkills();
    } catch (err: any) {
      setPanelError(err.response?.data?.message || t("app.skills.saveFailed"));
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editSkill) return;

    setDeleting(true);
    setPanelError(null);
    try {
      await skillService.deleteSkill(editSkill.id);
      setPanelMode("none");
      setSelectedSkillId(null);
      setEditSkill(null);
      await loadSkills();
    } catch (err: any) {
      setPanelError(
        err.response?.data?.message || t("app.skills.deleteFailed"),
      );
    } finally {
      setDeleting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Stack gap={4} w="full" maxW="full">
      <Flex justify="space-between" align="center">
        <Heading>{t("app.skills.title")}</Heading>
        <HStack gap={3}>
          <AskStrikeUpButton context={t("app.askStrikeUp.context.skills")} />
          <RefreshButton onClick={loadSkills} loading={loadingSkills} />
          <Button intent="secondary" onClick={startManualCreate}>
            <MdAdd />
            {t("app.skills.createManually")}
          </Button>
          <Button intent="primary" onClick={startAiCreate}>
            <MdAutoAwesome />
            {t("app.skills.createWithAi")}
          </Button>
        </HStack>
      </Flex>

      <Flex gap={4} h="calc(100vh - 100px)" align="stretch">
        {/* ── Left panel: skill list ──────────────────────────────────────────── */}
        <Card.Root w="340px" flexShrink={0} overflow="hidden">
          <Flex
            px={3}
            py={3}
            borderBottom="1px solid"
            borderColor={getThemeColor("border.default")}
            align="center"
            gap={2}
          >
            <MdSearch color="gray" />
            <Input
              type="text"
              size="sm"
              value={skillSearch}
              onChange={(e) => setSkillSearch(e.target.value)}
              placeholder={t("app.skills.searchPlaceholder")}
              aria-label={t("app.common.search")}
            />
          </Flex>
          <Card.Body p={0} overflow="auto">
            {/* Keep the list on screen while Refresh reloads it; only the first load shows the placeholder. */}
            {loadingSkills && skills.length === 0 ? (
              <Flex justify="center" align="center" minH="200px">
                <Text color={getThemeColor("text.secondary")}>
                  {t("app.common.loading")}
                </Text>
              </Flex>
            ) : skillsError ? (
              <Flex p={4}>
                <Text color="red.500">{skillsError}</Text>
              </Flex>
            ) : skills.length === 0 ? (
              <Flex justify="center" align="center" minH="200px" px={4}>
                <VStack gap={2} textAlign="center">
                  <MdAutoAwesome size={32} color="gray" />
                  <Text color={getThemeColor("text.secondary")}>
                    {t("app.skills.empty")}
                  </Text>
                  <Text fontSize="sm" color={getThemeColor("text.secondary")}>
                    {t("app.skills.emptyHint")}
                  </Text>
                </VStack>
              </Flex>
            ) : filteredSkills.length === 0 ? (
              <Flex justify="center" align="center" minH="120px" px={4}>
                <Text
                  fontSize="sm"
                  color={getThemeColor("text.secondary")}
                  textAlign="center"
                >
                  {t("app.skills.noSearchResults")}
                </Text>
              </Flex>
            ) : (
              <VStack gap={0} align="stretch">
                {filteredSkills.map((skill) => (
                  <Flex
                    key={skill.id}
                    px={4}
                    py={3}
                    cursor="pointer"
                    borderBottom="1px solid"
                    borderColor={getThemeColor("border.default")}
                    bg={
                      selectedSkillId === skill.id
                        ? getThemeColor("sidebar.activeLink")
                        : "transparent"
                    }
                    _hover={{ bg: getThemeColor("sidebar.hoverLink") }}
                    onClick={() => handleSkillSelect(skill)}
                    direction="column"
                    gap={1}
                  >
                    <HStack justify="space-between">
                      <Text fontWeight="semibold" fontSize="sm" truncate>
                        {skill.name}
                      </Text>
                      {skill.category && (
                        <Badge colorPalette="blue" size="sm" flexShrink={0}>
                          {skill.category}
                        </Badge>
                      )}
                    </HStack>
                    <Text
                      fontSize="xs"
                      color={getThemeColor("text.secondary")}
                      lineClamp={2}
                    >
                      {skill.triggerContext}
                    </Text>
                  </Flex>
                ))}
              </VStack>
            )}
          </Card.Body>
        </Card.Root>

        {/* ── Right panel ─────────────────────────────────────────────────────── */}
        <Card.Root
          flex="1"
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          {panelMode === "none" && (
            <Flex justify="center" align="center" flex="1">
              <VStack gap={3} textAlign="center">
                <MdAutoAwesome size={48} color="gray" />
                <Text color={getThemeColor("text.secondary")}>
                  {t("app.skills.selectOrCreate")}
                </Text>
              </VStack>
            </Flex>
          )}

          {/* View: read-only skill preview */}
          {panelMode === "view" && editSkill && (
            <Flex direction="column" flex="1" overflow="auto">
              <Flex
                px={4}
                py={3}
                borderBottom="1px solid"
                borderColor={getThemeColor("border.default")}
              >
                <HStack justify="space-between" w="full">
                  <HStack gap={2}>
                    <Heading size="md">{editSkill.name}</Heading>
                    {editSkill.category && (
                      <Badge colorPalette="blue">{editSkill.category}</Badge>
                    )}
                  </HStack>
                  <HStack gap={2}>
                    <Button
                      colorPalette="blue"
                      size="sm"
                      onClick={() => openEdit(editSkill)}
                    >
                      <MdEdit />
                      {t("app.skills.edit.button")}
                    </Button>
                    <DeleteConfirmPopover
                      onConfirm={handleDelete}
                      loading={deleting}
                      title={t("app.skills.delete.confirmTitle")}
                      message={t("app.skills.delete.confirmIrreversible")}
                      triggerLabel={t("app.skills.delete.button")}
                    />
                  </HStack>
                </HStack>
              </Flex>

              <Card.Body>
                <VStack gap={5} align="stretch">
                  <Stack gap={1}>
                    <Text fontSize="sm" fontWeight="semibold">
                      {t("app.skills.description")}
                    </Text>
                    <Text
                      fontSize="sm"
                      color={getThemeColor("text.secondary")}
                      whiteSpace="pre-wrap"
                    >
                      {editSkill.humanReadableDescription}
                    </Text>
                  </Stack>

                  <Stack gap={1}>
                    <Text fontSize="sm" fontWeight="semibold">
                      {t("app.skills.triggerContext")}
                    </Text>
                    <Card.Root bg={getThemeColor("sidebar.bg")}>
                      <Card.Body py={2} px={3}>
                        <Text
                          fontSize="sm"
                          color={getThemeColor("text.secondary")}
                        >
                          {editSkill.triggerContext}
                        </Text>
                      </Card.Body>
                    </Card.Root>
                  </Stack>

                  {editSkill.skillPrompt && (
                    <Stack gap={1}>
                      <Text fontSize="sm" fontWeight="semibold">
                        {t("app.skills.skillPrompt")}
                      </Text>
                      <Card.Root bg={getThemeColor("sidebar.bg")}>
                        <Card.Body py={2} px={3}>
                          <Text
                            fontSize="sm"
                            color={getThemeColor("text.secondary")}
                            whiteSpace="pre-wrap"
                          >
                            {editSkill.skillPrompt}
                          </Text>
                        </Card.Body>
                      </Card.Root>
                    </Stack>
                  )}

                  <Stack gap={1}>
                    <Text fontSize="sm" fontWeight="semibold">
                      {t("app.skills.status")}
                    </Text>
                    <Badge
                      colorPalette={editSkill.isActive ? "green" : "gray"}
                      w="fit-content"
                    >
                      {editSkill.isActive
                        ? t("app.skills.active")
                        : t("app.skills.inactive")}
                    </Badge>
                  </Stack>
                </VStack>
              </Card.Body>
            </Flex>
          )}

          {/* Create: wizard chat */}
          {panelMode === "create" && createMode === "ai" && (
            <Flex direction="column" flex="1" overflow="hidden">
              <Flex
                px={4}
                py={3}
                borderBottom="1px solid"
                borderColor={getThemeColor("border.default")}
              >
                <Heading size="md">{t("app.skills.create.title")}</Heading>
              </Flex>

              {/* Chat messages */}
              <Flex
                direction="column"
                flex="1"
                minH="0"
                overflow="auto"
                p={4}
                gap={3}
              >
                {wizardMessages.map((msg, i) => (
                  <Flex
                    key={i}
                    justify={msg.role === "user" ? "flex-end" : "flex-start"}
                  >
                    <Card.Root
                      maxW="80%"
                      bg={
                        msg.role === "user"
                          ? "blue.500"
                          : getThemeColor("card.bg")
                      }
                      color={msg.role === "user" ? "white" : "inherit"}
                    >
                      <Card.Body py={2} px={3}>
                        <Text fontSize="sm" whiteSpace="pre-wrap">
                          {msg.content.replace(READY_MARKER, "").trimEnd()}
                        </Text>
                      </Card.Body>
                    </Card.Root>
                  </Flex>
                ))}
                {wizardLoading && (
                  <Flex justify="flex-start">
                    <Card.Root bg={getThemeColor("card.bg")}>
                      <Card.Body py={2} px={3}>
                        <Text
                          fontSize="sm"
                          color={getThemeColor("text.secondary")}
                        >
                          {t("app.skills.wizard.thinking")}
                        </Text>
                      </Card.Body>
                    </Card.Root>
                  </Flex>
                )}
                <div ref={chatEndRef} />
              </Flex>

              {/* Error */}
              {panelError && (
                <Flex px={4} pb={2}>
                  <Text color="red.500" fontSize="sm">
                    {panelError}
                  </Text>
                </Flex>
              )}

              {/* Create Skill button — appears when AI signals readiness */}
              {isReadyToCreate && (
                <Flex px={4} pb={2} justify="flex-end">
                  <Button
                    colorPalette="green"
                    onClick={handleCompileAndSave}
                    loading={saving}
                    disabled={saving || wizardLoading}
                  >
                    {t("app.skills.create.save")}
                  </Button>
                </Flex>
              )}

              {/* Chat input */}
              <Flex px={4} pb={4} gap={2}>
                <Textarea
                  flex="1"
                  placeholder={t("app.skills.wizard.placeholder")}
                  value={wizardInput}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setWizardInput(e.target.value)
                  }
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendWizardMessage();
                    }
                  }}
                  rows={2}
                  resize="none"
                  disabled={wizardLoading || saving}
                />
                <Button
                  colorPalette="blue"
                  onClick={sendWizardMessage}
                  loading={wizardLoading}
                  disabled={!wizardInput.trim() || saving}
                  h="auto"
                  alignSelf="stretch"
                >
                  <MdSend />
                </Button>
              </Flex>
            </Flex>
          )}

          {/* Create: manual form (no AI) */}
          {panelMode === "create" && createMode === "manual" && (
            <Flex direction="column" flex="1" overflow="auto">
              <Flex
                px={4}
                py={3}
                borderBottom="1px solid"
                borderColor={getThemeColor("border.default")}
              >
                <Heading size="md">
                  {t("app.skills.create.manualTitle")}
                </Heading>
              </Flex>

              <Card.Body>
                <VStack gap={4} align="stretch">
                  {/* Name + Category */}
                  <HStack gap={3} align="flex-start">
                    <Stack flex="1" gap={1}>
                      <Text fontSize="sm" fontWeight="semibold">
                        {t("app.skills.name")}
                      </Text>
                      <Input
                        placeholder={t("app.skills.namePlaceholder")}
                        value={manualName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setManualName(e.target.value)
                        }
                        maxLength={SKILL_NAME_MAX}
                      />
                    </Stack>
                    <Stack flex="1" gap={1}>
                      <Text fontSize="sm" fontWeight="semibold">
                        {t("app.skills.category")}
                      </Text>
                      <Input
                        placeholder={t("app.skills.categoryPlaceholder")}
                        value={manualCategory}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setManualCategory(e.target.value)
                        }
                        maxLength={SKILL_CATEGORY_MAX}
                      />
                    </Stack>
                  </HStack>

                  {/* Description */}
                  <Stack gap={1}>
                    <Text fontSize="sm" fontWeight="semibold">
                      {t("app.skills.description")}
                    </Text>
                    <Text fontSize="xs" color={getThemeColor("text.secondary")}>
                      {t("app.skills.descriptionHint")}
                    </Text>
                    <Textarea
                      placeholder={t("app.skills.descriptionPlaceholder")}
                      value={manualDescription}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setManualDescription(e.target.value)
                      }
                      rows={5}
                      resize="vertical"
                    />
                  </Stack>

                  {/* Trigger context */}
                  <Stack gap={1}>
                    <Text fontSize="sm" fontWeight="semibold">
                      {t("app.skills.triggerContext")}
                    </Text>
                    <Text fontSize="xs" color={getThemeColor("text.secondary")}>
                      {t("app.skills.triggerContextHint")}
                    </Text>
                    <Textarea
                      placeholder={t("app.skills.triggerPlaceholder")}
                      value={manualTrigger}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setManualTrigger(e.target.value)
                      }
                      rows={2}
                      resize="vertical"
                      maxLength={SKILL_TRIGGER_MAX}
                    />
                    <CharCount value={manualTrigger} max={SKILL_TRIGGER_MAX} />
                  </Stack>

                  {/* Agent instructions (skill prompt) */}
                  <Stack gap={1}>
                    <Text fontSize="sm" fontWeight="semibold">
                      {t("app.skills.skillPrompt")}
                    </Text>
                    <Text fontSize="xs" color={getThemeColor("text.secondary")}>
                      {t("app.skills.skillPromptManualHint")}
                    </Text>
                    <Textarea
                      placeholder={t("app.skills.skillPromptPlaceholder")}
                      value={manualSkillPrompt}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setManualSkillPrompt(e.target.value)
                      }
                      rows={6}
                      resize="vertical"
                    />
                  </Stack>

                  {/* Error */}
                  {panelError && (
                    <Text color="red.500" fontSize="sm">
                      {panelError}
                    </Text>
                  )}

                  {/* Save */}
                  <HStack justify="flex-end">
                    <Button
                      intent="primary"
                      onClick={handleManualCreateSave}
                      loading={manualSaving}
                      disabled={manualSaving}
                    >
                      {t("app.skills.createSave")}
                    </Button>
                  </HStack>
                </VStack>
              </Card.Body>
            </Flex>
          )}

          {/* Edit: Option B — description textarea + change request */}
          {panelMode === "edit" && editSkill && (
            <Flex direction="column" flex="1" overflow="auto">
              <Flex
                px={4}
                py={3}
                borderBottom="1px solid"
                borderColor={getThemeColor("border.default")}
              >
                <HStack justify="space-between" w="full">
                  <Heading size="md">{editSkill.name}</Heading>
                  <DeleteConfirmPopover
                    onConfirm={handleDelete}
                    loading={deleting}
                    title={t("app.skills.delete.confirmTitle")}
                    message={t("app.skills.delete.confirmIrreversible")}
                    triggerLabel={t("app.skills.delete.button")}
                  />
                </HStack>
              </Flex>

              <Card.Body>
                <VStack gap={4} align="stretch">
                  {/* Manual / AI mode toggle */}
                  <Stack gap={2}>
                    <HStack justify="space-between" align="center" gap={4}>
                      <Text fontSize="sm" fontWeight="semibold">
                        {t("app.skills.editModeLabel")}
                      </Text>
                      <HStack
                        gap={1}
                        p={1}
                        borderRadius="full"
                        bg={getThemeColor("sidebar.bg")}
                        borderWidth="1px"
                        borderColor={getThemeColor("border.default")}
                        flexShrink={0}
                      >
                        <Button
                          size="xs"
                          borderRadius="full"
                          intent={editMode === "manual" ? "primary" : "ghost"}
                          onClick={() => {
                            setEditMode("manual");
                            setPanelError(null);
                          }}
                        >
                          {t("app.skills.mode.manual")}
                        </Button>
                        <Button
                          size="xs"
                          borderRadius="full"
                          intent={editMode === "ai" ? "primary" : "ghost"}
                          onClick={() => {
                            setEditMode("ai");
                            setPanelError(null);
                          }}
                        >
                          {t("app.skills.mode.ai")}
                        </Button>
                      </HStack>
                    </HStack>
                    <Text fontSize="xs" color={getThemeColor("text.secondary")}>
                      {t("app.skills.editModeHint")}
                    </Text>
                  </Stack>

                  {/* Name + Category */}
                  <HStack gap={3} align="flex-start">
                    <Stack flex="1" gap={1}>
                      <Text fontSize="sm" fontWeight="semibold">
                        {t("app.skills.name")}
                      </Text>
                      <Input
                        placeholder={t("app.skills.namePlaceholder")}
                        value={editName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setEditName(e.target.value)
                        }
                        maxLength={SKILL_NAME_MAX}
                      />
                    </Stack>
                    <Stack flex="1" gap={1}>
                      <Text fontSize="sm" fontWeight="semibold">
                        {t("app.skills.category")}
                      </Text>
                      <Input
                        placeholder={t("app.skills.categoryPlaceholder")}
                        value={editCategory}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setEditCategory(e.target.value)
                        }
                        maxLength={SKILL_CATEGORY_MAX}
                      />
                    </Stack>
                  </HStack>

                  {/* Human readable description — editable in both modes */}
                  <Stack gap={1}>
                    <Text fontSize="sm" fontWeight="semibold">
                      {t("app.skills.description")}
                    </Text>
                    <Text fontSize="xs" color={getThemeColor("text.secondary")}>
                      {t("app.skills.descriptionHint")}
                    </Text>
                    <Textarea
                      value={editDescription}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setEditDescription(e.target.value)
                      }
                      rows={6}
                      resize="vertical"
                    />
                  </Stack>

                  {editMode === "manual" ? (
                    <>
                      {/* Trigger context — editable */}
                      <Stack gap={1}>
                        <Text fontSize="sm" fontWeight="semibold">
                          {t("app.skills.triggerContext")}
                        </Text>
                        <Text
                          fontSize="xs"
                          color={getThemeColor("text.secondary")}
                        >
                          {t("app.skills.triggerContextHint")}
                        </Text>
                        <Textarea
                          placeholder={t("app.skills.triggerPlaceholder")}
                          value={editTrigger}
                          onChange={(
                            e: React.ChangeEvent<HTMLTextAreaElement>,
                          ) => setEditTrigger(e.target.value)}
                          rows={2}
                          resize="vertical"
                          maxLength={SKILL_TRIGGER_MAX}
                        />
                        <CharCount
                          value={editTrigger}
                          max={SKILL_TRIGGER_MAX}
                        />
                      </Stack>

                      {/* Agent instructions (skill prompt) — editable */}
                      <Stack gap={1}>
                        <Text fontSize="sm" fontWeight="semibold">
                          {t("app.skills.skillPrompt")}
                        </Text>
                        <Text
                          fontSize="xs"
                          color={getThemeColor("text.secondary")}
                        >
                          {t("app.skills.skillPromptManualHint")}
                        </Text>
                        <Textarea
                          placeholder={t("app.skills.skillPromptPlaceholder")}
                          value={editSkillPrompt}
                          onChange={(
                            e: React.ChangeEvent<HTMLTextAreaElement>,
                          ) => setEditSkillPrompt(e.target.value)}
                          rows={6}
                          resize="vertical"
                        />
                      </Stack>

                      {/* Error */}
                      {panelError && (
                        <Text color="red.500" fontSize="sm">
                          {panelError}
                        </Text>
                      )}

                      {/* Save (manual — direct, no AI) */}
                      <HStack justify="flex-end">
                        <Button
                          intent="primary"
                          onClick={handleManualEditSave}
                          loading={editSaving}
                          disabled={editSaving}
                        >
                          {t("app.skills.save")}
                        </Button>
                      </HStack>
                    </>
                  ) : (
                    <>
                      {/* Trigger context (read-only preview) */}
                      <Stack gap={1}>
                        <Text fontSize="sm" fontWeight="semibold">
                          {t("app.skills.triggerContext")}
                        </Text>
                        <Text
                          fontSize="xs"
                          color={getThemeColor("text.secondary")}
                        >
                          {t("app.skills.triggerContextHint")}
                        </Text>
                        <Card.Root bg={getThemeColor("sidebar.bg")}>
                          <Card.Body py={2} px={3}>
                            <Text
                              fontSize="sm"
                              color={getThemeColor("text.secondary")}
                            >
                              {compiledResult?.triggerContext ??
                                editSkill.triggerContext}
                            </Text>
                          </Card.Body>
                        </Card.Root>
                      </Stack>

                      {/* Skill prompt (read-only — regenerated by AI on save) */}
                      {(compiledResult?.skillPrompt ??
                        editSkill.skillPrompt) && (
                        <Stack gap={1}>
                          <Text fontSize="sm" fontWeight="semibold">
                            {t("app.skills.skillPrompt")}
                          </Text>
                          <Text
                            fontSize="xs"
                            color={getThemeColor("text.secondary")}
                          >
                            {t("app.skills.skillPromptHint")}
                          </Text>
                          <Card.Root bg={getThemeColor("sidebar.bg")}>
                            <Card.Body py={2} px={3}>
                              <Text
                                fontSize="sm"
                                color={getThemeColor("text.secondary")}
                                whiteSpace="pre-wrap"
                              >
                                {compiledResult?.skillPrompt ??
                                  editSkill.skillPrompt}
                              </Text>
                            </Card.Body>
                          </Card.Root>
                        </Stack>
                      )}

                      {/* Change request */}
                      <Stack gap={1}>
                        <Text fontSize="sm" fontWeight="semibold">
                          {t("app.skills.changeRequest")}
                        </Text>
                        <Text
                          fontSize="xs"
                          color={getThemeColor("text.secondary")}
                        >
                          {t("app.skills.changeRequestHint")}
                        </Text>
                        <Textarea
                          placeholder={t("app.skills.changeRequestPlaceholder")}
                          value={changeRequest}
                          onChange={(
                            e: React.ChangeEvent<HTMLTextAreaElement>,
                          ) => setChangeRequest(e.target.value)}
                          rows={3}
                          resize="vertical"
                        />
                      </Stack>

                      {/* Error */}
                      {panelError && (
                        <Text color="red.500" fontSize="sm">
                          {panelError}
                        </Text>
                      )}

                      {/* Save (AI — recompile) */}
                      <HStack justify="flex-end">
                        {(editDescription !==
                          editSkill.humanReadableDescription ||
                          changeRequest.trim()) && (
                          <Text
                            fontSize="xs"
                            color={getThemeColor("text.secondary")}
                          >
                            {t("app.skills.recompileNotice")}
                          </Text>
                        )}
                        <Button
                          intent="primary"
                          onClick={handleEditSave}
                          loading={editSaving}
                          disabled={editSaving}
                        >
                          {t("app.skills.save")}
                        </Button>
                      </HStack>
                    </>
                  )}
                </VStack>
              </Card.Body>
            </Flex>
          )}
        </Card.Root>
      </Flex>
    </Stack>
  );
};

export default Skills;
